import { useState, useCallback, useRef, useEffect } from 'react';
import { AxiosError } from 'axios';
import { handleApiError, ProcessedError } from '../utils/errorHandler';

interface UseApiCallOptions {
  onSuccess?: (data: any) => void;
  onError?: (error: ProcessedError) => void;
  showErrorToast?: boolean;
  retryCount?: number;
  retryDelay?: number;
}

interface UseApiCallResult<T> {
  data: T | null;
  loading: boolean;
  error: ProcessedError | null;
  execute: (...args: any[]) => Promise<T | null>;
  reset: () => void;
  retry: () => Promise<T | null>;
}

export function useApiCall<T = any>(
  apiFunction: (...args: any[]) => Promise<T>,
  options: UseApiCallOptions = {}
): UseApiCallResult<T> {
  const {
    onSuccess,
    onError,
    showErrorToast = true,
    retryCount = 0,
    retryDelay = 1000,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ProcessedError | null>(null);
  
  const lastArgsRef = useRef<any[]>([]);
  const retryCountRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(async (...args: any[]): Promise<T | null> => {
    if (!isMountedRef.current) return null;

    lastArgsRef.current = args;
    retryCountRef.current = 0;
    
    setLoading(true);
    setError(null);

    try {
      const result = await apiFunction(...args);
      
      if (!isMountedRef.current) return null;
      
      setData(result);
      setLoading(false);
      
      if (onSuccess) {
        onSuccess(result);
      }
      
      return result;
    } catch (err) {
      if (!isMountedRef.current) return null;
      
      const processedError = handleApiError(err, {
        function: apiFunction.name,
        args: args,
        timestamp: new Date().toISOString(),
      });
      setError(processedError);
      setLoading(false);

      // Handle specific error codes
      if (processedError.code === 'UNAUTHORIZED') {
        // Token expired, redirect to login
        localStorage.removeItem('access_token');
        const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                         window.location.pathname.startsWith('/dev') ? '/dev' : '';
        window.location.href = basename + '/login';
        return null;
      }

      // Retry logic for certain errors
      if (
        retryCountRef.current < retryCount &&
        processedError.canRetry
      ) {
        retryCountRef.current++;
        
        // Exponential backoff
        const delay = retryDelay * Math.pow(2, retryCountRef.current - 1);
        
        setTimeout(async () => {
          if (isMountedRef.current) {
            await execute(...args);
          }
        }, delay);
        
        return null;
      }

      if (onError) {
        onError(processedError);
      }

      return null;
    }
  }, [apiFunction, onSuccess, onError, showErrorToast, retryCount, retryDelay]);

  const retry = useCallback(async (): Promise<T | null> => {
    if (lastArgsRef.current.length > 0) {
      return execute(...lastArgsRef.current);
    }
    return null;
  }, [execute]);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    retryCountRef.current = 0;
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
    retry,
  };
}


// Hook for multiple API calls - Commented out due to React hooks rules violation
// This function violates the rules of hooks by calling useApiCall inside a forEach loop
// If needed in the future, this should be refactored to use a different pattern
/*
interface UseMultipleApiCallsOptions extends UseApiCallOptions {
  parallel?: boolean;
}

export function useMultipleApiCalls<T extends Record<string, (...args: any[]) => Promise<any>>>(
  apiFunctions: T,
  options: UseMultipleApiCallsOptions = {}
): {
  [K in keyof T]: UseApiCallResult<Awaited<ReturnType<T[K]>>>;
} & {
  executeAll: (...args: Parameters<T[keyof T]>[]) => Promise<void>;
  loading: boolean;
  hasError: boolean;
  reset: () => void;
} {
  const { parallel = true, ...apiOptions } = options;
  
  const results: any = {};
  const apiCalls: UseApiCallResult<any>[] = [];
  
  // Create individual API call hooks
  Object.entries(apiFunctions).forEach(([key, apiFunction]) => {
    const apiCall = useApiCall(apiFunction, apiOptions);
    results[key] = apiCall;
    apiCalls.push(apiCall);
  });
  
  // Combined loading state
  const loading = apiCalls.some(call => call.loading);
  
  // Combined error state
  const hasError = apiCalls.some(call => call.error !== null);
  
  // Execute all API calls
  const executeAll = useCallback(async (...args: any[]) => {
    if (parallel) {
      // Execute in parallel
      await Promise.all(
        apiCalls.map(call => call.execute(...args))
      );
    } else {
      // Execute sequentially
      for (const call of apiCalls) {
        await call.execute(...args);
      }
    }
  }, [apiCalls, parallel]);
  
  // Reset all API calls
  const reset = useCallback(() => {
    apiCalls.forEach(call => call.reset());
  }, [apiCalls]);
  
  return {
    ...results,
    executeAll,
    loading,
    hasError,
    reset,
  };
}
*/

// Hook for paginated API calls
interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface UsePaginatedApiCallResult<T> extends UseApiCallResult<T> {
  pagination: PaginationState;
  goToPage: (page: number) => Promise<void>;
  nextPage: () => Promise<void>;
  previousPage: () => Promise<void>;
  changePageSize: (size: number) => Promise<void>;
}

export function usePaginatedApiCall<T = any>(
  apiFunction: (page: number, pageSize: number, ...args: any[]) => Promise<{
    data: T;
    pagination: {
      total: number;
      page: number;
      page_size: number;
      pages: number;
    };
  }>,
  initialPageSize: number = 20,
  options: UseApiCallOptions = {}
): UsePaginatedApiCallResult<T> {
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: initialPageSize,
    total: 0,
    totalPages: 0,
  });
  
  const apiCall = useApiCall(
    async (...args: any[]) => {
      const response = await apiFunction(pagination.page, pagination.pageSize, ...args);
      
      setPagination({
        page: response.pagination.page,
        pageSize: response.pagination.page_size,
        total: response.pagination.total,
        totalPages: response.pagination.pages,
      });
      
      return response.data;
    },
    options
  );
  
  const goToPage = useCallback(async (page: number) => {
    if (page < 1 || page > pagination.totalPages) return;
    
    setPagination(prev => ({ ...prev, page }));
    await apiCall.execute();
  }, [pagination.totalPages, apiCall]);
  
  const nextPage = useCallback(async () => {
    await goToPage(pagination.page + 1);
  }, [pagination.page, goToPage]);
  
  const previousPage = useCallback(async () => {
    await goToPage(pagination.page - 1);
  }, [pagination.page, goToPage]);
  
  const changePageSize = useCallback(async (size: number) => {
    setPagination(prev => ({ ...prev, pageSize: size, page: 1 }));
    await apiCall.execute();
  }, [apiCall]);
  
  return {
    ...apiCall,
    pagination,
    goToPage,
    nextPage,
    previousPage,
    changePageSize,
  };
}

// Hook for infinite scroll API calls
interface UseInfiniteScrollResult<T> {
  data: T[];
  loading: boolean;
  error: ProcessedError | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  reset: () => void;
  refresh: () => Promise<void>;
}

export function useInfiniteScroll<T = any>(
  apiFunction: (page: number, pageSize: number, ...args: any[]) => Promise<{
    data: T[];
    hasMore: boolean;
  }>,
  pageSize: number = 20,
  options: UseApiCallOptions = {}
): UseInfiniteScrollResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ProcessedError | null>(null);
  
  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiFunction(page, pageSize);
      
      setData(prev => [...prev, ...response.data]);
      setHasMore(response.hasMore);
      setPage(prev => prev + 1);
      
      if (options.onSuccess) {
        options.onSuccess(response.data);
      }
    } catch (err) {
      const processedError = handleApiError(err, {
        function: 'useInfiniteScroll',
        page,
        pageSize,
      });
      setError(processedError);
      
      if (options.onError) {
        options.onError(processedError);
      }
    } finally {
      setLoading(false);
    }
  }, [apiFunction, page, pageSize, loading, hasMore, options]);
  
  const reset = useCallback(() => {
    setData([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setLoading(false);
  }, []);
  
  const refresh = useCallback(async () => {
    reset();
    await loadMore();
  }, [reset, loadMore]);
  
  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    reset,
    refresh,
  };
}